// src/components/NotificationBell.js

import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, onSnapshot, query, where, collection, orderBy, writeBatch, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { Badge, Tooltip, IconButton, Menu, MenuItem, Typography, Box, Button, Snackbar, Alert } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { initializePushNotifications, setupForegroundListener } from '../utils/fcmUtils';

const NotificationBell = () => {
    const { currentUser } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser) return;
        const q = query(
            collection(db, "notifications"),
            where("userId", "==", currentUser.uid),
            orderBy("timestamp", "desc")
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setNotifications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [currentUser]);

    useEffect(() => {
        if ('Notification' in window) {
            setPushEnabled(Notification.permission === 'granted');
        }
    }, []);

    useEffect(() => {
        if (!currentUser || !pushEnabled) return;

        const unsubscribe = setupForegroundListener((payload) => {
            setSnackbar({
                open: true,
                message: payload.notification?.body || 'Yeni bildirim',
                severity: 'info'
            });
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [currentUser, pushEnabled]);

    const handleEnablePush = useCallback(async () => {
        if (!currentUser) return;

        setPushLoading(true);
        try {
            const result = await initializePushNotifications(currentUser.uid);

            if (result.success) {
                setPushEnabled(true);
                setSnackbar({
                    open: true,
                    message: 'Push bildirimleri aktif edildi!',
                    severity: 'success'
                });
            } else {
                let message = 'Push bildirimleri etkinleştirilemedi.';
                if (result.reason === 'permission_denied') {
                    message = 'Bildirim izni reddedildi. Tarayıcı ayarlarından izin verin.';
                } else if (result.reason === 'not_supported') {
                    message = 'Bu tarayıcı push bildirimleri desteklemiyor.';
                }
                setSnackbar({ open: true, message, severity: 'warning' });
            }
        } catch (error) {
            console.error('Push aktifleştirme hatası:', error);
            setSnackbar({
                open: true,
                message: 'Bir hata oluştu.',
                severity: 'error'
            });
        } finally {
            setPushLoading(false);
        }
    }, [currentUser]);

    const unreadCount = notifications.filter(notif => !notif.isRead).length;

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleMarkAsRead = async (id, link) => {
        if (id) {
            const docRef = doc(db, "notifications", id);
            await updateDoc(docRef, { isRead: true });
        }
        if (link) navigate(link);
        handleClose();
    };

    const handleMarkAllAsRead = async () => {
        const batch = writeBatch(db);
        notifications.forEach(notif => {
            if (!notif.isRead) {
                const docRef = doc(db, "notifications", notif.id);
                batch.update(docRef, { isRead: true });
            }
        });
        await batch.commit();
    };

    const handleDeleteNotification = async (e, notificationId) => {
        e.stopPropagation();
        if (!notificationId) return;
        try {
            const docRef = doc(db, "notifications", notificationId);
            await deleteDoc(docRef);
        } catch (error) {
            console.error("Bildirim silinirken hata:", error);
        }
    };

    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    return (
        <Box>
            <Tooltip title={pushEnabled ? "Bildirimler (Push Aktif)" : "Bildirimler"}>
                <IconButton color="inherit" onClick={handleClick}>
                    <Badge badgeContent={unreadCount} color="error">
                        {pushEnabled ? <NotificationsActiveIcon /> : <NotificationsIcon />}
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{ maxHeight: 450, minWidth: 320 }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {!pushEnabled && (
                    <Box sx={{ p: 1.5, backgroundColor: 'info.light', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'info.contrastText' }}>
                            Push bildirimleri ile anında haberdar olun
                        </Typography>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<NotificationsActiveIcon />}
                            onClick={handleEnablePush}
                            disabled={pushLoading}
                            fullWidth
                        >
                            {pushLoading ? 'Aktifleştiriliyor...' : 'Bildirimleri Aç'}
                        </Button>
                    </Box>
                )}
                {notifications.length > 0 ? [
                    notifications.slice(0, 10).map(notif => (
                        <MenuItem
                            key={notif.id}
                            onClick={() => handleMarkAsRead(notif.id, notif.link)}
                            sx={{
                                whiteSpace: 'normal',
                                borderBottom: '1px solid #eee',
                                backgroundColor: notif.isRead ? 'transparent' : 'action.hover'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Box sx={{ pr: 1 }}>
                                    <Typography variant="body1" fontWeight="bold">{notif.title}</Typography>
                                    <Typography variant="body2" sx={{ my: 0.5 }}>{notif.message}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {notif.timestamp ? formatDistanceToNow(notif.timestamp.toDate(), { addSuffix: true, locale: tr }) : ''}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleDeleteNotification(e, notif.id)}
                                    title="Bu bildirimi sil"
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </MenuItem>
                    )),
                    <Box key="actions" sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                        <Button size="small" onClick={handleMarkAllAsRead}>Tümünü Okundu İşaretle</Button>
                    </Box>
                ] : (
                    <MenuItem onClick={handleClose}>
                        <Typography>Yeni bildirim yok.</Typography>
                    </MenuItem>
                )}
            </Menu>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default NotificationBell;